import type {ClickSubscriptionPlanTableProperties} from '@gaia/ampli';
import {GaiaColorHexes} from '@gaia/colors';
import type {SharedData, SharedUserData} from '@gaia/types';
import {
    type CoordinateFormat,
    coordinateFormatSchema,
    type UnitSystem,
    unitSystemSchema,
} from '@gaia/types/User';

export function isMarkerType(value: string): value is MarkerType {
    switch (value as MarkerType) {
        case 'pin':
        case 'outlined-icon':
            return true;
    }

    // TS thinks the `switch` is exhaustive because of the type assertion, and we want the
    // assertion so that TS enforces exhaustiveness among our `case` statements. We handle the
    // possibility that it's not exhaustive by returning `false`, which TS claims is unreachable
    // code. We could make it "reachable" by removing the `as MarkerType` assertion, but then we
    // lose the exhaustiveness check, and we'd then need a `@ts-ignore` directive to mitigate the
    // resulting TS error, whereas unreachable code doesn't cause a warning or error that needs to
    // be suppressed.

    return false;
}

export function isGaiaColorHex(colorHex: string): colorHex is GaiaColorHex {
    return GaiaColorHexes.includes(colorHex as GaiaColorHex);
}

export function assertGaiaColorHex(colorHex: string): asserts colorHex is GaiaColorHex {
    if (!GaiaColorHexes.includes(colorHex as GaiaColorHex)) {
        throw new Error(`not a recognized Gaia color: ${colorHex}`);
    }
}

export function assertMarkerType(markerType: string): asserts markerType is MarkerType {
    switch (markerType as MarkerType) {
        case 'outlined-icon':
        case 'pin':
            return;
    }

    // See `isMarkerType` comment.

    throw new Error(`Not a valid marker type: ${markerType}`);
}

export function isControlPointImageType(value: string): value is ControlPointImageType {
    return value === 'control-point';
}

export function isControlPointType(value: string): value is ControlPointType {
    const types: ControlPointType[] = ['start', 'middle', 'end'];
    return types.includes(value as ControlPointType);
}

export function assertControlPointType(value: string): asserts value is ControlPointType {
    if (!isControlPointType(value)) throw new Error(`${value} is not start|middle|end`);
}

export function isControlPointState(value: string): value is ControlPointState {
    const controlPointStates: ControlPointState[] = ['idle', 'selected'];
    return controlPointStates.includes(value as ControlPointState);
}

export function assertControlPointState(value: string): asserts value is ControlPointState {
    if (!isControlPointState(value)) throw new Error(`${value} is not idle|selected`);
}

export function isSharedUserData(sharedData: SharedData): sharedData is SharedUserData {
    if (sharedData === undefined) return false;
    if (
        sharedData.type === 'waypoint' ||
        sharedData.type === 'area' ||
        sharedData.type === 'track' ||
        sharedData.type === 'folder'
    )
        return true;
    return false;
}

type ControlPointImageType = 'control-point';

export type ControlPointType = 'start' | 'end' | 'middle';

export type ControlPointState = 'idle' | 'selected';

export type MapboxImageType = MarkerType | ControlPointImageType;

/**
 * For use with Array.filter to remove `null` and `undefined` from an array and let TypeScript
 * correctly infer the resulting type. Semi-replacement for lodash.compact when it is used to
 * compact a sparse array where holes are nullish.
 *
 * With type predicate:
 *
 * `const arr = [0, 1, null, 2, undefined].filter(filterNonNullable);` => `number[]`
 *
 * Without:
 *
 * `const arr = [0, 1, null, 2, undefined].filter((v) => v !== null && v !== undefined);`
 * => `(number | null | undefined)[]`
 */
export function filterNonNullable<T>(v: T): v is NonNullable<T> {
    return v !== null && v !== undefined;
}

export function assertUnitSystem(unitSystem: string): asserts unitSystem is UnitSystem {
    unitSystemSchema.parse(unitSystem); // throws
}

export function assertCoordinateFormat(format: string): asserts format is CoordinateFormat {
    coordinateFormatSchema.parse(format); // throws
}

export function assertIsSubscriptionPlanTableLocation(
    location: string
): asserts location is ClickSubscriptionPlanTableProperties['subscriptionPlanTableLocation'] {
    const subscriptionPlanTableLocations: ClickSubscriptionPlanTableProperties['subscriptionPlanTableLocation'][] =
        [
            'homepage',
            'go-premium-modal',
            '/overland/',
            '/backpacking/',
            '/canada/',
            '/hunting/',
            '/fishing/',
            '/ski/',
            '/mountain-biking/',
            '/hiking/',
            '/membership/',
            '/outsideplus/',
            'other-hunting-page',
        ];

    if (
        !subscriptionPlanTableLocations.includes(
            location as ClickSubscriptionPlanTableProperties['subscriptionPlanTableLocation']
        )
    ) {
        throw new Error(`Invalid Subscription Plan Table location: ${location}`);
    }
}
